import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { User } from '../../models/User';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AppContext } from '../../window';
import { useTranslation } from '../../i18n';

interface GuestSlideProps {
    onUnlockPrompt: (fromHeart?: boolean, profileName?: string, userId?: number) => void;
    onSelectionComplete: (genderId: number, agegroupId: number) => void;
    selectedGenderId?: number;
    selectedAgegroupId?: number;
    onProfileView?: () => void;
    profileViewCount?: number;
}

interface SelectionOption {
    id: number;
    value: string;
    label: string;
    icon: string;
}

export const GuestSlide: React.FC<GuestSlideProps> = ({ 
    onUnlockPrompt, 
    onSelectionComplete,
    selectedGenderId,
    selectedAgegroupId,
    onProfileView,
    profileViewCount = 0
}) => {
    const { t } = useTranslation();
    const { location, data } = useContext(AppContext);
    
    // Process gender and age group options from attributes
    const genderOptions = useMemo<SelectionOption[]>(() => {
        if (!data?.attributes) return [];
        
        return data.attributes
            .filter(attr => attr.name === 'gender')
            .filter(attr => attr.value === 'Female' || attr.value === 'Shemale')
            .map(attr => ({
                id: attr.id,
                value: attr.value.toLowerCase(),
                label: attr.value === 'Female' ? 'Women' : 'Trans',
                icon: attr.value === 'Female' ? '♀️' : '⚧️'
            }));
    }, [data]);
    
    const ageGroupOptions = useMemo<SelectionOption[]>(() => {
        if (!data?.attributes) return [];
        
        return data.attributes
            .filter(attr => attr.name === 'agegroup')
            .map(attr => ({
                id: attr.id,
                value: attr.value,
                label: attr.translatedValue || attr.value,
                icon: ''
            }))
            .sort((a, b) => {
                // Sort age groups in ascending order
                const aNum = parseInt(a.value.split('-')[0]);
                const bNum = parseInt(b.value.split('-')[0]);
                return aNum - bNum;
            });
    }, [data]);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [hoveredArea, setHoveredArea] = useState<{ x: number; y: number } | null>(null);
    const [showGenderSelection, setShowGenderSelection] = useState(true);
    const [showAgeSelection, setShowAgeSelection] = useState(false);
    const [tempGenderId, setTempGenderId] = useState<number>(0);
    const [profileOffset, setProfileOffset] = useState(0);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [autoRotateTimer, setAutoRotateTimer] = useState<NodeJS.Timeout | null>(null);
    const [isAutoRotating, setIsAutoRotating] = useState(true);
    const [initialBackgroundProfile, setInitialBackgroundProfile] = useState<User | null>(null);

    // Load initial background profile on mount (default to female)
    useEffect(() => {
        const loadInitialProfile = async () => {
            try {
                // Random offset between 0 and 10 for initial profile
                const randomOffset = Math.floor(Math.random() * 11);
                
                const params = new URLSearchParams({
                    limit: '1',
                    offset: randomOffset.toString(),
                    gender: 'female' // Default to female profiles
                });
                
                // Add location params for cache busting
                if (location) {
                    if (location.country?.iso) {
                        params.append('countryIso', location.country.iso);
                    }
                    if (location.province?.iso) {
                        params.append('provinceIso', location.province.iso);
                    }
                    if (location.town?.name) {
                        params.append('town', location.town.name);
                    }
                }
                
                const response = await fetch(`/user/slider/json?${params.toString()}`);
                const data = await response.json();
                
                if (data && data.id) {
                    setInitialBackgroundProfile(data);
                } else if (Array.isArray(data) && data.length > 0 && data[0].id) {
                    setInitialBackgroundProfile(data[0]);
                }
            } catch (error) {
                console.error('Error loading initial background profile:', error);
            }
        };
        
        loadInitialProfile();
    }, []);

    useEffect(() => {
        // Load user when both selections are made
        if (selectedGenderId && selectedAgegroupId) {
            setShowGenderSelection(false);
            setShowAgeSelection(false);
            loadRandomUser();
            // Start auto-rotation after selections
            setIsAutoRotating(true);
        }
    }, [selectedGenderId, selectedAgegroupId]);

    // Auto-rotate profiles every 5 seconds
    useEffect(() => {
        if (isAutoRotating && selectedGenderId && selectedAgegroupId && user && !loading && profileViewCount < 3) {
            const timer = setTimeout(() => {
                loadRandomUser();
                if (onProfileView) {
                    onProfileView();
                }
            }, 7500);
            setAutoRotateTimer(timer);
            return () => {
                if (timer) clearTimeout(timer);
            };
        }
        // Return undefined for the false case
        return undefined;
    }, [user, isAutoRotating, selectedGenderId, selectedAgegroupId, loading, profileViewCount]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (autoRotateTimer) {
                clearTimeout(autoRotateTimer);
            }
        };
    }, []);

    const loadRandomUser = async () => {
        try {
            // Only show loading if we don't have a user yet
            if (!user) {
                setLoading(true);
            }
            
            // Use the current offset, but increment it for next time
            const currentOffset = isInitialLoad ? 0 : profileOffset;
            
            // Build query parameters
            const params = new URLSearchParams({
                limit: '1',
                offset: currentOffset.toString()
            });
            
            if (selectedGenderId) {
                const selectedGender = genderOptions.find(opt => opt.id === selectedGenderId);
                if (selectedGender) {
                    params.append('gender', selectedGender.value);
                }
            }
            
            if (selectedAgegroupId) {
                const selectedAgegroup = ageGroupOptions.find(opt => opt.id === selectedAgegroupId);
                if (selectedAgegroup) {
                    params.append('agegroup', selectedAgegroup.value);
                }
            }
            
            // Add location params for cache busting
            if (location) {
                if (location.country?.iso) {
                    params.append('_country', location.country.iso);
                }
                if (location.province?.iso) {
                    params.append('_province', location.province.iso);
                }
                if (location.town?.name) {
                    params.append('_town', location.town.name);
                }
            }
            
            const response = await fetch(`/user/slider/json?${params.toString()}`);
            const data = await response.json();
            
            if (data && data.id) {
                setUser(data);
                // Increment offset for next profile
                if (isInitialLoad) {
                    setIsInitialLoad(false);
                    setProfileOffset(1);
                } else {
                    setProfileOffset(prev => prev + 1);
                }
            } else if (Array.isArray(data) && data.length > 0 && data[0].id) {
                // Handle array response
                setUser(data[0]);
                // Increment offset for next profile
                if (isInitialLoad) {
                    setIsInitialLoad(false);
                    setProfileOffset(1);
                } else {
                    setProfileOffset(prev => prev + 1);
                }
            } else {
                console.error('Unexpected data format:', data);
                // Reset offset if no more profiles
                setProfileOffset(0);
                setIsInitialLoad(true);
            }
        } catch (error) {
            console.error('Error loading user:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadBackgroundProfileByGender = async (genderValue: string) => {
        try {
            // Random offset between 0 and 10
            const randomOffset = Math.floor(Math.random() * 11);
            
            const params = new URLSearchParams({
                limit: '1',
                offset: randomOffset.toString(),
                gender: genderValue
            });
            
            // Add location params for cache busting
            if (location) {
                if (location.country?.iso) {
                    params.append('_country', location.country.iso);
                }
                if (location.province?.iso) {
                    params.append('_province', location.province.iso);
                }
                if (location.town?.name) {
                    params.append('_town', location.town.name);
                }
            }
            
            const response = await fetch(`/user/slider/json?${params.toString()}`);
            const data = await response.json();
            
            if (data && data.id) {
                setInitialBackgroundProfile(data);
            } else if (Array.isArray(data) && data.length > 0 && data[0].id) {
                setInitialBackgroundProfile(data[0]);
            }
        } catch (error) {
            console.error('Error loading background profile:', error);
        }
    };

    const handleGenderSelect = async (genderId: number) => {
        setTempGenderId(genderId);
        
        // Find the selected gender option
        const selectedGender = genderOptions.find(opt => opt.id === genderId);
        
        // If Trans is selected, immediately load a shemale profile
        if (selectedGender && selectedGender.value === 'shemale') {
            await loadBackgroundProfileByGender('shemale');
        }
        
        setShowGenderSelection(false);
        setShowAgeSelection(true);
    };

    const handleAgeSelect = (agegroupId: number) => {
        setShowAgeSelection(false);
        onSelectionComplete(tempGenderId, agegroupId);
        // Reset offset for new selection
        setProfileOffset(0);
        setIsInitialLoad(true);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setHoveredArea({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    const handleMouseLeave = () => {
        setHoveredArea(null);
    };

    // Show selection overlays if no selections made yet
    if (!selectedGenderId || !selectedAgegroupId) {
        return (
            <div className="guest-slide position-relative rounded shadow-lg overflow-hidden mx-auto" style={{ borderRadius: '16px', minHeight: '600px' }}>
                <div className="slide-wrapper position-relative" style={{ borderRadius: '16px', overflow: 'hidden', height: '600px' }}>
                    {/* Background image from initial profile */}
                    {initialBackgroundProfile && initialBackgroundProfile.displayPicture?.large && (
                        <img
                            src={initialBackgroundProfile.displayPicture.large}
                            alt=""
                            aria-hidden="true"
                            fetchPriority="high"
                            decoding="async"
                            className="position-absolute w-100 h-100"
                            style={{ 
                                objectFit: 'cover',
                                objectPosition: 'center',
                                filter: 'blur(2px)',
                                transform: 'scale(1.1)', // Prevent blur edge artifacts
                                zIndex: 0
                            }}
                        />
                    )}
                    {/* Gender selection overlay */}
                    {showGenderSelection && !selectedGenderId && (
                        <div 
                            className="selection-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
                            style={{ 
                                backdropFilter: 'blur(3px)', 
                                zIndex: 30,
                                borderRadius: '16px'
                            }}>
                            <div className="text-center px-3 py-4 w-100" style={{ maxWidth: '350px' }}>
                                <h4 className="mb-3">{t('landing.looking_for')}</h4>
                                <div className="row g-2">
                                    {genderOptions.map((option) => (
                                        <div key={option.value} className="col-6">
                                            <button
                                                className="btn btn-light w-100 py-3 rounded-pill"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleGenderSelect(option.id);
                                                }}
                                            >
                                                {option.icon && <span className="d-block fs-3 mb-1">{option.icon}</span>}
                                                <span className={option.icon ? '' : 'fs-5'}>{option.label}</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Age selection overlay */}
                    {showAgeSelection && !selectedAgegroupId && (
                        <div 
                            className="selection-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
                            style={{ 
                                backdropFilter: 'blur(3px)', 
                                zIndex: 30,
                                borderRadius: '16px'
                            }}>
                            <div className="text-center px-3 py-4 w-100" style={{ maxWidth: '350px' }}>
                                <h4 className="mb-3">{t('landing.age_preference')}</h4>
                                <div className="row g-2">
                                    {ageGroupOptions.map((option) => (
                                        <div key={option.value} className="col-6">
                                            <button
                                                className="btn btn-light w-100 py-3 rounded-pill"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAgeSelect(option.id);
                                                }}
                                            >
                                                {option.icon && <span className="d-block fs-3 mb-1">{option.icon}</span>}
                                                <span className={option.icon ? '' : 'fs-5'}>{option.label}</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (loading || !user) {
        return (
            <div className="guest-slide guest-slide-loading d-flex align-items-center justify-content-center mx-auto rounded bg-body-tertiary" style={{ minHeight: '600px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="guest-slide position-relative rounded shadow-lg overflow-hidden mx-auto" style={{ borderRadius: '16px' }}>
            {/* Main slide container */}
            <div className="slide-wrapper position-relative" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                {/* Background image - subtle blur to encourage signup */}
                                {user.displayPicture?.large && (
                                    <img
                                        src={user.displayPicture.large}
                                        alt=""
                                        aria-hidden="true"
                                        fetchPriority="low"
                                        decoding="async"
                                        className="position-absolute w-100 h-100"
                                        style={{ 
                                            objectFit: 'cover',
                                            objectPosition: 'center',
                                            filter: 'blur(3px)',
                                            transform: 'scale(1.05)', // Prevent blur edge artifacts
                                            transition: 'opacity 0.5s ease-in-out'
                                        }}
                                    />
                                )}
                
                {/* Auto-rotation indicator */}
                {isAutoRotating && profileViewCount < 3 && (
                    <div className="position-absolute top-0 start-0 w-100" style={{ zIndex: 20 }}>
                        <div className="progress" style={{ height: '3px', backgroundColor: 'transparent' }}>
                            <div 
                                className="progress-bar bg-primary" 
                                role="progressbar" 
                                style={{ 
                                    width: '100%',
                                    animation: 'progress-countdown 7.5s linear infinite'
                                }}
                            />
                        </div>
                    </div>
                )}
                
                {/* Gender selection overlay */}
                {showGenderSelection && !selectedGenderId && (
                    <div 
                        className="selection-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
                        style={{ 
                            backdropFilter: 'blur(8px)', 
                            zIndex: 30,
                            transition: 'opacity 0.3s ease-out',
                            borderRadius: '16px'
                        }}>
                        <div className="text-center px-3 py-4 w-100" style={{ maxWidth: '350px' }}>
                            <h4 className="mb-3">I'm looking for...</h4>
                            <div className="row g-2">
                                {genderOptions.map((option) => (
                                    <div key={option.value} className="col-6">
                                        <button
                                            className="btn btn-outline-light w-100 py-3 rounded-pill"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleGenderSelect(option.id);
                                            }}
                                        >
                                            <span className="d-block fs-3 mb-1">{option.icon}</span>
                                            <span>{option.label}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Age selection overlay */}
                {showAgeSelection && !selectedAgegroupId && (
                    <div 
                        className="selection-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
                        style={{ 
                            backdropFilter: 'blur(4px)', 
                            zIndex: 30,
                            transition: 'opacity 0.3s ease-out',
                            borderRadius: '16px'
                        }}>
                        <div className="text-center px-3 py-4 w-100" style={{ maxWidth: '350px' }}>
                            <h4 className="mb-3">Age preference?</h4>
                            <div className="row g-2">
                                {ageGroupOptions.map((option) => (
                                    <div key={option.value} className="col-6">
                                        <button
                                            className="btn btn-outline-light w-100 py-3 rounded-pill"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAgeSelect(option.id);
                                            }}
                                        >
                                            <span className="d-block fs-3 mb-1">{option.icon}</span>
                                            <span>{option.label}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Interactive overlay with match info */}
                {selectedGenderId && selectedAgegroupId && (
                    <>
                        {/* Match percentage badge */}
                        <div className="position-absolute top-0 end-0 m-3" style={{ zIndex: 15 }}>
                            <div className="badge bg-success px-3 py-2 shadow">
                                <VisibilityIcon fontSize="small" className="me-1" />
                                {t('landing.match_percentage', { percent: Math.floor(Math.random() * 15) + 80 })}
                            </div>
                        </div>
                        
                        {/* Online status */}
                        <div className="position-absolute top-0 start-0 m-3" style={{ zIndex: 15 }}>
                            <div className="badge bg-light text-dark px-3 py-2 shadow">
                                <span className="text-success me-1">●</span>
                                {t('landing.online_now')}
                            </div>
                        </div>
                    </>
                )}

                {/* Interactive CTA overlay - appears on hover */}
                <div 
                    className="position-absolute bottom-0 start-0 w-100 p-4"
                    style={{ 
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        cursor: 'pointer',
                        zIndex: 10,
                        transform: 'translateY(0)'
                    }}
                    onClick={() => {
                        setIsAutoRotating(false);
                        onUnlockPrompt(false);
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        setIsAutoRotating(false);
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0';
                        setIsAutoRotating(true);
                    }}
                >
                    <div className="text-center">
                        <h5 className="text-white mb-2">{t('landing.like_what_you_see')}</h5>
                        <p className="text-white-50 mb-3 small">
                            {t('landing.join_to_chat_with', { nickname: user.nickname || t('landing.this_member') })}
                        </p>
                        <Button 
                            variant="primary" 
                            size="lg"
                            className="shadow"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsAutoRotating(false);
                                onUnlockPrompt(false);
                            }}
                        >
                            {t('landing.start_chatting_now')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* User info below image - only show after selections are made */}
            {selectedGenderId && selectedAgegroupId && (
                <>
                    <div className="bg-body p-3 border-bottom">
                        <h5 className="mb-1">
                            {user.nickname || 'Anonymous'}, {user.age || '??'}
                        </h5>
                        <p className="text-muted small mb-2 d-flex align-items-center">
                            <LocationOnIcon fontSize="small" className="me-1" />
                            {user.town || 'Unknown'}, {user.province?.name || 'Unknown'}
                        </p>
                        {user.description && (
                            <p className="text-muted small mb-0">
                                {user.description.substring(0, 150)}...
                            </p>
                        )}
                    </div>
                    
                    {/* Action buttons area - simulated match actions */}
                    <div className="bg-body p-3" style={{ borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
                        <div className="d-flex justify-content-center gap-3">
                            <Button 
                                variant="outline-danger"
                                className="rounded-circle"
                                style={{ width: '50px', height: '50px' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsAutoRotating(false);
                                    
                                    // Check if we've hit the limit
                                    if (profileViewCount >= 2) {
                                        // This will be the 3rd profile
                                        if (onProfileView) {
                                            onProfileView();
                                        }
                                    } else {
                                        loadRandomUser();
                                        if (onProfileView) {
                                            onProfileView();
                                        }
                                        setTimeout(() => setIsAutoRotating(true), 1000);
                                    }
                                }}
                            >
                                <CloseIcon />
                            </Button>
                            <Button 
                                variant="success"
                                className="rounded-circle shadow pulse-animation"
                                style={{ width: '60px', height: '60px' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsAutoRotating(false);
                                    // Pass heart click info with user's name and ID
                                    onUnlockPrompt(true, user.nickname || 'this member', user.id);
                                }}
                            >
                                <FavoriteIcon />
                            </Button>
                            <Button 
                                variant="outline-primary"
                                className="rounded-circle"
                                style={{ width: '50px', height: '50px' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsAutoRotating(false);
                                    onUnlockPrompt(false);
                                }}
                            >
                                <MoreVertIcon />
                            </Button>
                        </div>
                        <p className="text-center text-muted small mt-2 mb-0">
                            {profileViewCount < 3 ? (
                                <>Profile {profileViewCount + 1} of 3 • {isAutoRotating ? 'Auto-playing' : 'Paused'}</>
                            ) : (
                                'Sign up to see more profiles!'
                            )}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};